import React, { useState, useEffect } from 'react';

interface Goal {
  GoalId?: number;
  GoalText: string;
}

interface FocusArea {
  AreaId?: number;
  AreaName: string;
  Description?: string;
}

export default function StrategicGoalsManager() {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [focusAreas, setFocusAreas] = useState<FocusArea[]>([]);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showAreaForm, setShowAreaForm] = useState(false);
  const [goalText, setGoalText] = useState('');
  const [areaData, setAreaData] = useState({ areaName: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const goalsRes = await fetch('/strategic/goals');
      const goalsData = await goalsRes.json();
      setGoals(goalsData.data || []);

      const areasRes = await fetch('/strategic/focus-areas');
      const areasData = await areasRes.json();
      setFocusAreas(areasData.data || []);
    } catch (err) {
      console.error('Failed to fetch strategic data:', err);
    }
  };

  const handleAddGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/strategic/goals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goalText }),
      });
      const data = await res.json();
      if (data.success) {
        setGoalText('');
        setShowGoalForm(false);
        fetchData();
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleAddArea = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/strategic/focus-areas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(areaData),
      });
      const data = await res.json();
      if (data.success) {
        setAreaData({ areaName: '', description: '' });
        setShowAreaForm(false);
        fetchData();
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2>Yearly Goals</h2>
        <button
          className="btn-primary"
          onClick={() => setShowGoalForm(!showGoalForm)}
          style={{ marginBottom: '1rem' }}
        >
          {showGoalForm ? 'Cancel' : '+ Add Goal'}
        </button>

        {showGoalForm && (
          <form onSubmit={handleAddGoal} style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
            <textarea
              value={goalText}
              onChange={(e) => setGoalText(e.target.value)}
              placeholder="Enter goal text..."
              required
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', minHeight: '80px' }}
            />
            <button type="submit" className="btn-primary">Create Goal</button>
          </form>
        )}

        <div>
          {goals.length === 0 ? (
            <p style={{ color: '#666' }}>No yearly goals yet</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {goals.map((goal, idx) => (
                <li key={idx} style={{ background: 'white', padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}>
                  {goal.GoalText}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <h2>6-Month Focus Areas</h2>
        <button
          className="btn-primary"
          onClick={() => setShowAreaForm(!showAreaForm)}
          style={{ marginBottom: '1rem' }}
        >
          {showAreaForm ? 'Cancel' : '+ Add Focus Area'}
        </button>

        {showAreaForm && (
          <form onSubmit={handleAddArea} style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
            <input
              type="text"
              value={areaData.areaName}
              onChange={(e) => setAreaData({ ...areaData, areaName: e.target.value })}
              placeholder="e.g., Customer Experience"
              required
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem' }}
            />
            <textarea
              value={areaData.description}
              onChange={(e) => setAreaData({ ...areaData, description: e.target.value })}
              placeholder="Description (optional)"
              style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', minHeight: '60px' }}
            />
            <button type="submit" className="btn-primary">Create Focus Area</button>
          </form>
        )}

        <div>
          {focusAreas.length === 0 ? (
            <p style={{ color: '#666' }}>No focus areas yet</p>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {focusAreas.map((area, idx) => (
                <div key={idx} style={{ background: 'white', padding: '1rem', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>{area.AreaName}</h4>
                  {area.Description && <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>{area.Description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
